import React, { type ErrorInfo } from 'react';
import { clientMount } from '@vp/ubik-fragment-react';
import { type MountWithRootOptions } from '@vp/ubik-fragment-types';
import { IdentityProvider, useSwanStyleKeys } from '@vp/ubik-context';
import { getComponent, type ComponentKey } from '../ubik-entry/mapping';
import { createTrackingConfiguration } from './tracking-configuration';
import { createAuthConfig } from '../ubik-entry/auth';
import { isCompressedNavigationData } from '../common/utils/navDataValidator';
import { decompressNavigationData } from '../common/utils/navDataDecompressor';
import { safeDecompress } from '../common/utils/navDataValidator';
import { Logger } from '../common/utils/logger';
import { SearchComponentProvider } from '../common/contexts/SearchComponentContext';
import { swanStyleKeys } from '../common/utils/swanStyleKeys';

import type {
  FullHeaderProps,
  FullHeaderRawProps,
  SlimHeaderProps,
  FullFooterProps,
  SlimFooterProps,
  GenericSharedProps,
  HeaderCommonProps,
} from '../common/types';

type SearchBarProps = {
  forwardedPath?: string;
  isSiteSearchBarRolloutEnabled?: boolean;
};

type FlyoutRenderModeProps = {
  flyoutRenderMode?: 'default' | 'lazy-rerender' | 'no-ssr-flyout';
};

const searchBarInstance = 'full-header-search-bar';

/**
 * Wrapper component that calls useSwanStyleKeys and injects container styles for @vp/search-bar.
 * This is needed because:
 * 1. @vp/search-bar doesn't call useSwanStyleKeys internally
 * 2. @vp/search-bar doesn't include container layout styles (unlike @vp/omnisearch)
 */
const SearchBarWithStyles: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useSwanStyleKeys(swanStyleKeys);
  return <>{children}</>;
};

/**
 * Dynamically imports and creates the appropriate search bar component on the client.
 * This function is called during the mount phase, BEFORE hydration starts, ensuring
 * the component is ready when React hydrates.
 */
async function createClientSearchBarComponent(
  searchBarType: 'omnisearch' | 'ubik',
  headerProps: FullHeaderProps | FullHeaderRawProps,
  forwardedPath?: string,
  auth?: unknown
): Promise<React.ReactNode> {
  const searchLogger = {
    ...Logger.instance,
    log: (message: string) => {
      Logger.instance.info(message);
    },
    warn: (message: string) => {
      Logger.instance.warning(message);
    },
  };

  if (searchBarType === 'ubik') {
    const { SearchBar } = await import('@vp/search-bar');
    return (
      <SearchBarWithStyles>
        <SearchBar
          logger={searchLogger}
          locale={headerProps.locale}
          pathname={forwardedPath || '/search'}
          searchLabel={headerProps.searchLabel}
        />
      </SearchBarWithStyles>
    );
  } else {
    const { SearchBar } = await import('@vp/omnisearch');
    return (
      <SearchBar
        auth={auth}
        locale={headerProps.locale}
        searchLabel={headerProps.searchLabel}
        instanceId={searchBarInstance}
        flyoutEnabled={headerProps.searchTypeaheadData?.enabled}
        logger={Logger.instance}
      />
    );
  }
}

type ComponentPropsMap = {
  'full-header': (FullHeaderProps | FullHeaderRawProps) & HeaderCommonProps & GenericSharedProps & SearchBarProps & FlyoutRenderModeProps;
  'slim-header': SlimHeaderProps & GenericSharedProps;
  'full-footer': FullFooterProps & GenericSharedProps;
  'slim-footer': SlimFooterProps & GenericSharedProps;
};

interface SSRProps<T extends ComponentKey = ComponentKey> {
  type: T;
  locale: string;
  base: ComponentPropsMap[T];
  forwardedPath?: string;
  isSiteSearchBarRolloutEnabled?: boolean;
  searchBarType?: 'omnisearch' | 'ubik';
  searchBarComponent?: React.ReactNode;
}

const selectComponent = <T extends ComponentKey>(props: SSRProps<T>) => {
  const trackingConfig = createTrackingConfiguration();

  const Component = getComponent(props.type);

  const componentElement = (
    <Component {...props} trackingConfiguration={trackingConfig} />
  );

  return () => (
    <IdentityProvider auth={createAuthConfig(props.locale)}>
      {props.searchBarComponent ? (
        <SearchComponentProvider searchBarComponent={props.searchBarComponent} searchBarType={props.searchBarType}>
          {componentElement}
        </SearchComponentProvider>
      ) : (
        componentElement
      )}
    </IdentityProvider>
  );
};

export const mount: MountWithRootOptions<SSRProps> = async (
  rootElement,
  ssrProps,
  rootOptions
) => {
  // Decompress navigation data if compressed
  let decompressedProps: SSRProps = ssrProps;

  if (ssrProps.type === 'full-header') {
    const headerProps = ssrProps as SSRProps<'full-header'>;
    const nav = headerProps.base.navigation;

    // Only try to decompress if navigation is a Record (not an Array) and is compressed
    if (nav && !Array.isArray(nav) && isCompressedNavigationData(nav)) {
      const decompressionResult = safeDecompress(
        () => decompressNavigationData(nav),
        nav
      );

      if (decompressionResult.success) {
        decompressedProps = {
          ...headerProps,
          base: {
            ...headerProps.base,
            navigation: decompressionResult.data.navigation,
            topLevelNavNodes: decompressionResult.data.topLevelNavNodes,
            topLevelNavNodeTests: decompressionResult.data.topLevelNavNodeTests,
          },
        };

      }
    }

    // Create search bar component dynamically using the type passed from server
    // This ensures we load the exact same search bar that was rendered during SSR
    const auth = createAuthConfig(headerProps.locale);
    const searchBarType = headerProps.searchBarType || 'omnisearch';

    const searchBarComponent = await createClientSearchBarComponent(
      searchBarType,
      headerProps.base,
      headerProps.forwardedPath,
      auth
    );

    // Inject search bar component as a top-level prop (not in base)
    // This matches how the server passes it in SSRComponent
    decompressedProps = {
      ...decompressedProps,
      searchBarComponent,
    } as SSRProps;
  }

  const Component = selectComponent(decompressedProps);


  rootOptions.hydrateOptions = {
    onCaughtError: (error: Error, errorInfo: ErrorInfo) => {
      const cause = 'cause' in error ? error.cause : undefined;
      console.error('[react-bookends-fragment] onCaughtError', {
        error,
        cause,
        componentStack: errorInfo.componentStack,
      });
    },
    onUncaughtError: (error: Error, errorInfo: ErrorInfo) => {
      const cause = 'cause' in error ? error.cause : undefined;
      console.error('[react-bookends-fragment] onUncaughtError', {
        error,
        cause,
        componentStack: errorInfo.componentStack,
      });
    },
    onRecoverableError: (error: Error, errorInfo: ErrorInfo) => {
      const cause = 'cause' in error ? error.cause : undefined;
      console.error('[react-bookends-fragment] onRecoverableError', {
        error,
        cause,
        componentStack: errorInfo.componentStack,
      });
    },
  };

  return clientMount(<Component />, rootElement, rootOptions);
};
